import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { receiptApproved, receiptDraftSendIn, receiptDraftUpdated, receiptUpdated } from "store/actions/receiptActions";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { ReceiptFooterProps } from "models/propTypes/receiptPropTypes";
import { TranslationKeys } from "translations";
import { ActionButton } from "models/propTypes/commonPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { PageFooter } from "components/common";
import ReceiptMoreActions from "./receiptMoreActions";
import useEditableState from "./useReadOnlyState";
import useReceiptTodoHelpers from "./useTodoHelpers";
import { ReceiptService } from "services";
import { useReceiptService } from "serviceHooks";
import { useSaveCostForwardingMarking } from "components/costForwardingMarking";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const ReceiptFooter: React.FC<ReceiptFooterProps> = (props) => {
  const receiptService = useReceiptService();
  const { reloadReceipt, markAsDone, receipt, disableSuccessMessages } = props;
  const dispatch = useDispatch();
  const isReadOnly = useEditableState(props.receipt.state, props.receipt.createdByPersonId);
  const isReceiptDraft = receipt.state === WintSuperkollAPIModel.ReceiptStateApiModel.Draft;

  const { updateReceiptTodo } = useReceiptTodoHelpers();
  const saveWithCostForwardingMarking = useSaveCostForwardingMarking("receipt");

  const isSoftValidation =
    receipt.state === WintSuperkollAPIModel.ReceiptStateApiModel.Draft &&
    receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard;

  const acceptReceipt = () => {
    return props.receiptFormSubmitHandler(async (values) => {
      return saveWithCostForwardingMarking(props.receipt.id, values, () =>
        receiptService.sign(props.receipt.id, receiptService.getUpdatedReceipt(props.receipt, values))
      );
    });
  };

  const receiptAcceptSuccess = () => {
    if (isReceiptDraft) {
      dispatch(receiptDraftSendIn(props.receipt.id));
      markAsDone();
    } else {
      reloadReceipt((r) =>
        updateReceiptTodo(
          r,
          (r) => dispatch(receiptUpdated(r)),
          () => dispatch(receiptApproved(props.receipt.id))
        )
      );
    }
  };

  const updateReceipt = () => {
    return props.receiptFormSubmitHandler((values) => {
      return saveWithCostForwardingMarking(props.receipt.id, values, () =>
        receiptService.put(props.receipt.id, receiptService.getUpdatedReceipt(props.receipt, values, isSoftValidation))
      );
    }, isSoftValidation);
  };

  const receiptUpdateSuccess = () => {
    reloadReceipt((r) => dispatch(isReceiptDraft ? receiptDraftUpdated(r) : receiptUpdated(r)));
  };

  const getActionButtons = (): ActionButton[] => {
    const buttons: ActionButton[] = [];
    if (!isReadOnly) {
      !props.hideSave &&
        buttons.push({
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
          onClick: updateReceipt,
          type: StyleEnums.DisplayType.Info,
          successMessage: TranslationKeys.Receipt.ReceiptSavedSuccessMessage,
          onActionSuccess: receiptUpdateSuccess,
        });

      buttons.push({
        text: LocalizationHelperInstance.getFormattedMessage(
          receipt.state === WintSuperkollAPIModel.ReceiptStateApiModel.Draft ||
            ReceiptService.getReceiptTodoSubType(receipt.state, receipt.sendBackData?.message) ===
              WintSuperkollAPIModel.TodoSubType.ReceiptSentBack
            ? TranslationKeys.Receipt.SendIn
            : TranslationKeys.Common.Approve
        ),
        onClick: acceptReceipt,
        type: StyleEnums.DisplayType.Success,
        successMessage:
          receipt.state === WintSuperkollAPIModel.ReceiptStateApiModel.Draft
            ? TranslationKeys.Receipt.DraftSendSuccess
            : TranslationKeys.Receipt.ReceiptAcceptedSuccessMessage,
        onActionSuccess: receiptAcceptSuccess,
      });
    }

    return buttons;
  };

  return (
    <PageFooter
      actions={getActionButtons()}
      moreActions={
        <ReceiptMoreActions
          receipt={props.receipt}
          reloadReceipt={reloadReceipt}
          markAsDone={markAsDone}
          costForwardingAction={props.costForwardingAction}
        />
      }
      disableSuccessMessages={disableSuccessMessages}
    />
  );
};

export default ReceiptFooter;
